// 3-Monats-Datums-Picker als Popover. Zeigt 3 Monate untereinander, jeden
// in einer eigenen Pastell-Tönung. Klick auf einen Tag wählt ihn aus.
//
// Benutzung im Builder: ein Knopf öffnet das Popover, beim Klick auf einen
// Tag wird der Wert via onPick zurückgegeben und das Popover schließt.

import { el } from './dom';

export type DatePickerOptions = {
  /** Initialwert ISO yyyy-mm-dd. */
  value: string;
  /** Anker — Popover positioniert sich relativ dazu. */
  anchor: HTMLElement;
  onPick: (iso: string) => void;
};

const MONTH_NAMES = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];
const WEEKDAY_NAMES = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
// Pastell pro Monatsoffset (current, +1, +2)
const MONTH_PASTELS = ['#fef3c7', '#dbeafe', '#fce7f3'];

export function openDateRangePicker(opts: DatePickerOptions): void {
  // Schließe vorherige offene Picker, falls noch offen.
  document.querySelectorAll('.drp-popover').forEach((n) => n.remove());

  const popover = el('div', { class: 'drp-popover', role: 'dialog' });
  const start = parseIso(opts.value);
  const baseYear = start.getFullYear();
  const baseMonth = start.getMonth();
  let cursorYear = baseYear;
  let cursorMonth = baseMonth;

  const navBar = el('div', { class: 'drp-nav' });
  const prevBtn = el('button', { type: 'button', class: 'drp-nav-btn', 'aria-label': 'Zurück' }, '‹');
  const nextBtn = el('button', { type: 'button', class: 'drp-nav-btn', 'aria-label': 'Weiter' }, '›');
  const today  = el('button', { type: 'button', class: 'drp-nav-btn drp-nav-today' }, 'Heute');
  navBar.append(prevBtn, today, nextBtn);
  popover.append(navBar);

  const monthsHost = el('div', { class: 'drp-months' });
  popover.append(monthsHost);
  document.body.append(popover);
  position(popover, opts.anchor);
  drawMonths();

  prevBtn.addEventListener('click', () => {
    cursorMonth--;
    if (cursorMonth < 0) { cursorMonth = 11; cursorYear--; }
    drawMonths();
  });
  nextBtn.addEventListener('click', () => {
    cursorMonth++;
    if (cursorMonth > 11) { cursorMonth = 0; cursorYear++; }
    drawMonths();
  });
  today.addEventListener('click', () => {
    const now = new Date();
    cursorYear = now.getFullYear();
    cursorMonth = now.getMonth();
    drawMonths();
  });

  const onOutside = (e: Event) => {
    if (!popover.contains(e.target as Node) && e.target !== opts.anchor && !opts.anchor.contains(e.target as Node)) {
      close();
    }
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') close();
  };
  setTimeout(() => {
    document.addEventListener('mousedown', onOutside);
    document.addEventListener('keydown', onKey);
  }, 0);

  function close(): void {
    document.removeEventListener('mousedown', onOutside);
    document.removeEventListener('keydown', onKey);
    popover.remove();
  }

  function drawMonths(): void {
    monthsHost.replaceChildren();
    for (let offset = 0; offset < 3; offset++) {
      const m = (cursorMonth + offset) % 12;
      const y = cursorYear + Math.floor((cursorMonth + offset) / 12);
      monthsHost.append(renderMonth(y, m, MONTH_PASTELS[offset], (iso) => {
        opts.onPick(iso);
        close();
      }, opts.value));
    }
  }
}

function renderMonth(year: number, month: number, pastel: string, onPick: (iso: string) => void, selected: string): HTMLElement {
  const wrap = el('div', { class: 'drp-month' });
  wrap.style.background = pastel;
  wrap.append(el('div', { class: 'drp-month-title' }, `${MONTH_NAMES[month]} ${year}`));

  const grid = el('div', { class: 'drp-grid' });
  for (const wd of WEEKDAY_NAMES) {
    grid.append(el('div', { class: 'drp-weekday' }, wd));
  }

  // Erster Tag im Monat — Wochentag (Mo=0..So=6)
  const firstDay = new Date(year, month, 1);
  const firstWeekday = (firstDay.getDay() + 6) % 7;  // konvertiert So=0..Sa=6 zu Mo=0..So=6
  for (let i = 0; i < firstWeekday; i++) {
    grid.append(el('div', { class: 'drp-day drp-day-empty' }));
  }

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const todayIso = isoDate(new Date());
  for (let d = 1; d <= daysInMonth; d++) {
    const iso = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const dayBtn = el('button', { type: 'button', class: 'drp-day' }, String(d)) as HTMLButtonElement;
    if (iso === todayIso) dayBtn.classList.add('is-today');
    if (iso === selected) dayBtn.classList.add('is-selected');
    const dt = new Date(year, month, d);
    const wd = dt.getDay();
    if (wd === 0 || wd === 6) dayBtn.classList.add('is-weekend');
    dayBtn.addEventListener('click', () => onPick(iso));
    grid.append(dayBtn);
  }
  wrap.append(grid);
  return wrap;
}

function position(popover: HTMLElement, anchor: HTMLElement): void {
  const r = anchor.getBoundingClientRect();
  // Versucht unten am Anker; falls nicht genug Platz, oben.
  popover.style.top = `${window.scrollY + r.bottom + 6}px`;
  popover.style.left = `${window.scrollX + r.left}px`;
  // Optional: nach dem ersten Render Höhe prüfen und ggf. nach oben verschieben.
  requestAnimationFrame(() => {
    const pop = popover.getBoundingClientRect();
    if (pop.bottom > window.innerHeight - 10) {
      popover.style.top = `${window.scrollY + Math.max(10, r.top - pop.height - 6)}px`;
    }
    if (pop.right > window.innerWidth - 10) {
      popover.style.left = `${window.scrollX + Math.max(10, window.innerWidth - pop.width - 10)}px`;
    }
  });
}

function parseIso(iso: string): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return new Date();
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

function isoDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
